/**
 * The Journal as a practice (2026-10-28,
 * `Claude outputs/journal-practice-2026-10-28/DESIGN.md`; contract API.md).
 *
 * The book's practice pieces, pure: the left page's contents (Your threads),
 * the month's chapter note, a thread (GET journal/thread/) and a chapter
 * (GET journal/chapter/) as the pages read them, and the copy-on-write edits
 * behind the one edit pattern (tap the text → a field in place → Done), so a
 * failed save can put the old book back. The day's own pieces (threads, own,
 * before, meaning) are read in `normalizeDay` (journal-book.js).
 */
import { keyDate } from '@/utils/journal-book'

const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const longNoYear = (k) => { const d = keyDate(k); return `${MON[d.getMonth()]} ${d.getDate()}` }

/** Nothing written yet on a reading. */
export const ownEmpty = (own) => !own || (!own.gut && !own.landed)

// The seeker's two lines, as labelled everywhere (Chris 2026-10-28): a label
// shows only over words; adding one is the reading's or step's ✎ menu.
export const OWN_LABEL = { gut: 'My hunch', landed: 'What I took from it' }
export const OWN_ADD = { gut: 'Add my hunch', landed: 'Add what I took from it' }
export const OWN_CHANGE = { gut: 'Change my hunch', landed: 'Change what I took from it' }
/** The lines a reading could still take ([] for a reading done for someone). */
export function missingOwn (o) {
  if (!o || o.forName) return []
  return ['gut', 'landed'].filter((p) => !o[p])
}

/** Words cut at a word, at most n characters, with an ellipsis. */
export function shortLine (t, n = 60) {
  const s = String(t || '').replace(/\s+/g, ' ').trim()
  if (s.length <= n) return s
  const cut = s.slice(0, n - 1)
  if (s[n - 1] === ' ') return cut.replace(/[\s,;:.]+$/, '') + '…'
  const i = cut.lastIndexOf(' ')
  return (i > n / 2 ? cut.slice(0, i) : cut).replace(/[\s,;:.]+$/, '') + '…'
}

// ── the left page: the contents (DESIGN §8, 2026-10-29) ──────────────────────
/** The newest loaded day on a thread (the calendar turns to its month to ring it). */
export function threadLatest (book, id) {
  const days = (book.days || []).filter((d) => (d.threads || []).some((t) => t.id === id) || (d.owns || []).some((o) => o.threadId === id))
  if (days.length) return days[days.length - 1].key
  const t = (book.threads || []).find((x) => x.id === id)
  return (t && t.lastKey) || ''
}

/** A thread's contents line from the loaded days (an older server sends none): their latest words, else the latest question. */
function lineFromDays (book, id) {
  const owns = []
  for (const d of book.days || []) for (const o of d.owns || []) if (o.threadId === id) owns.push(o)
  const mine = owns.filter((o) => !o.forName)
  for (const piece of ['landed', 'gut']) {
    const o = [...mine].reverse().find((x) => x[piece])
    if (o) return { line: o[piece], lineKind: 'own', forSomeone: false }
  }
  const q = [...owns].reverse().find((x) => x.question)
  if (q) return { line: q.question, lineKind: 'asked', forSomeone: !!q.forName }
  return { line: '', lineKind: '', forSomeone: false }
}

/**
 * Your threads (the left page's contents): `many` (two or more readings) and
 * `single` (one-offs, folded), each newest activity first:
 * [{ id, name, count, first, last, line, lineKind: 'own' | 'asked' | '', forSomeone }].
 */
export function threadRows (book) {
  let list = (book.threads || []).map((t) => ({ id: t.id, name: t.name, count: t.count, first: t.firstKey, last: t.lastKey, fromServer: t.lineKind !== undefined, line: t.line, lineKind: t.lineKind, forSomeone: t.forSomeone }))
  if (!list.length) {
    const seen = new Map()
    for (const d of book.days || []) {
      for (const t of d.threads || []) {
        const r = seen.get(t.id) || { id: t.id, name: t.name, count: 0, first: d.key, last: d.key, fromServer: false }
        r.count += Math.max(1, (d.owns || []).filter((o) => o.threadId === t.id).length)
        r.last = d.key
        seen.set(t.id, r)
      }
    }
    list = [...seen.values()]
  }
  const rows = list.map(({ fromServer, ...r }) => ({ ...r, ...(fromServer ? { line: r.line || '', lineKind: r.lineKind || '', forSomeone: !!r.forSomeone } : lineFromDays(book, r.id)) }))
    .sort((a, b) => (b.last || '').localeCompare(a.last || '') || (b.id > a.id ? 1 : -1))
  return { many: rows.filter((r) => r.count >= 2), single: rows.filter((r) => r.count < 2) }
}

const MON3 = (k) => MON[keyDate(k).getMonth()].slice(0, 3)
const YR = (k) => keyDate(k).getFullYear()
/** "8 readings · Jun–Oct"; the year only when it isn't this one: "Jul ’25–Oct". */
export function contentsSpan (r, today = '') {
  const n = r.count === 1 ? '1 reading' : `${r.count} readings`
  const last = r.last || r.first
  if (!last) return n
  const cy = today ? YR(today) : new Date().getFullYear()
  const yy = (k) => `’${String(YR(k)).slice(2)}`
  const end = MON3(last) + (YR(last) !== cy ? ' ' + yy(last) : '')
  const first = r.first || last
  if (first.slice(0, 7) === last.slice(0, 7)) return `${n} · ${end}`
  return `${n} · ${MON3(first)}${YR(first) !== YR(last) ? ' ' + yy(first) : ''}–${end}`
}

const NOT_YET = 'Its chapter is written when the month closes.'

/** The calendar's chapter state for a month (journal/calendar/ → chapter), kept on the book. */
export const withChapterState = (book, month, state) => ({ ...book, chapterStates: { ...(book.chapterStates || {}), [String(month).slice(0, 7)]: state } })

/** The chapter line under the calendar: "Read the chapter" once written; else when it will be; null when there will never be one. */
export function chapterNote (book, anyKeyInMonth) {
  const mk = String(anyKeyInMonth).slice(0, 7)
  const month = MON[keyDate(mk + '-01').getMonth()]
  const states = book.chapterStates || {}
  if (mk in states) {
    const st = states[mk]
    if (!st) return null
    if (st.written) return { month: st.title || month, ready: true, text: 'Read the chapter' }
    return { month, ready: false, text: NOT_YET }
  }
  // Not heard yet: only this month promises one.
  if (mk === String(book.today || '').slice(0, 7)) return { month, ready: false, text: NOT_YET }
  return null
}

/** GET journal/threads/ → [{ id, name, nameIsUser, count, firstKey, lastKey, line?, lineKind?, forSomeone? }] (the line once the server sends it). */
export function normalizeThreadList (raw) {
  const list = raw && raw.ok !== false && Array.isArray(raw.threads) ? raw.threads : []
  return list.filter((t) => t && t.id !== null && t.id !== undefined).map((t) => ({
    id: t.id, name: String(t.name || ''), nameIsUser: !!t.name_is_user, count: Number(t.count) || 0, firstKey: t.first_date || '', lastKey: t.last_date || '',
    ...('latest_kind' in t ? { line: String(t.latest_line || ''), lineKind: t.latest_kind || '', forSomeone: !!t.for_someone } : {}),
  }))
}

// ── a thread ────────────────────────────────────────────────────────────────
export function normalizeThread (raw) {
  const t = raw && raw.ok !== false ? (raw.thread || null) : null
  if (!t || t.id === null || t.id === undefined) return null
  const steps = (raw.steps || t.steps || []).map((s) => ({ key: String(s.date || ''), readingId: String(s.reading_id || ''), question: String(s.question || ''),
    forName: s.for_name ? String(s.for_name) : '', gut: String(s.gut || ''), landed: String(s.landed || ''), gutEdited: !!s.gut_edited, landedEdited: !!s.landed_edited }))
    .sort((a, b) => a.key.localeCompare(b.key))
  const arc = raw.arc && raw.arc.first && raw.arc.latest && raw.arc.first !== raw.arc.latest ? { first: String(raw.arc.first), latest: String(raw.arc.latest) } : null
  return {
    id: t.id, name: String(t.name || ''), nameIsUser: !!t.name_is_user,
    count: Number(t.count) || steps.length,
    firstKey: String(t.first_date || (steps[0] && steps[0].key) || ''),
    lastKey: String(t.last_date || (steps.length && steps[steps.length - 1].key) || ''),
    arc, steps,
  }
}

/** "6 readings · July 14 to October 28". */
export function threadSpan (t) {
  if (!t) return ''
  const n = t.count === 1 ? '1 reading' : `${t.count} readings`
  if (!t.firstKey) return n
  if (!t.lastKey || t.lastKey === t.firstKey) return `${n} · ${longNoYear(t.firstKey)}`
  return `${n} · ${longNoYear(t.firstKey)} to ${longNoYear(t.lastKey)}`
}

/** A step's date in the margin: 14 over Jul. */
export const stepDate = (k) => { const d = keyDate(k); return { day: d.getDate(), month: MON[d.getMonth()].slice(0, 3) } }

// ── the chapter ─────────────────────────────────────────────────────────────
export function normalizeChapter (raw) {
  const c = raw && raw.ok !== false ? (raw.chapter || null) : null
  if (!c || !c.month) return null
  const mk = String(c.month).slice(0, 7)
  return {
    month: mk, title: c.title || MON[keyDate(mk + '-01').getMonth()], edited: !!c.edited, text: String(c.text || ''), writtenAt: c.written_at || '',
    versions: Array.isArray(c.versions) ? c.versions : [],
    sections: (Array.isArray(c.sections) ? c.sections : []).map((s) => ({
      id: s.key || '', title: String(s.title || ''),
      lines: (s.lines || []).filter((l) => l && l.text).map((l) => ({ threadId: l.thread_id === undefined ? null : l.thread_id, thread: String(l.thread || ''), text: String(l.text), key: String(l.date || '') })),
    })),
  }
}

/** The chapter as one plain text (DESIGN §6: "editable like an entry"): their text once edited, else the sections. */
export function chapterPlainText (c) {
  if (!c) return ''
  if (c.edited && c.text) return c.text
  return c.sections.map((s) => [s.title, ...s.lines.map((l) => (l.thread ? `${l.thread}: ${l.text}` : l.text))].join('\n')).join('\n\n')
}

// ── edits (copy-on-write) ───────────────────────────────────────────────────
const mapDay = (book, key, fn) => ({ ...book, days: book.days.map((d) => (d.key === key ? fn(d) : d)) })

/** piece: 'gut' | 'landed'. */
export function withOwn (book, key, piece, text, readingId = '') {
  const set = (o) => (o && (!readingId || o.readingId === readingId) ? { ...o, [piece]: text, [piece + 'Edited']: true } : o)
  return mapDay(book, key, (d) => ({ ...d, own: set(d.own), owns: (d.owns || []).map(set) }))
}

export function withMeaning (book, key, threadId, text) {
  return mapDay(book, key, (d) => {
    const has = (d.meaning || []).some((m) => m.threadId === threadId)
    const meaning = has
      ? d.meaning.map((m) => (m.threadId === threadId ? { ...m, line: text, edited: true } : m))
      : [...(d.meaning || []), { threadId, thread: ((d.threads || []).find((t) => t.id === threadId) || {}).name || '', line: text, edited: true }]
    return { ...d, meaning }
  })
}

/** A rename follows the thread everywhere: every tagged day, every left-page line, the book's list. */
export function withThreadName (book, id, name) {
  return {
    ...book,
    threads: (book.threads || []).map((t) => (t.id === id ? { ...t, name } : t)),
    days: book.days.map((d) => ({
      ...d,
      threads: (d.threads || []).map((t) => (t.id === id ? { ...t, name } : t)),
      meaning: (d.meaning || []).map((m) => (m.threadId === id ? { ...m, thread: name } : m)),
    })),
  }
}

export function withDayThreads (book, key, threads) {
  const known = new Set((book.threads || []).map((t) => t.id))
  const added = threads.filter((t) => t.id && !known.has(t.id)).map((t) => ({ id: t.id, name: t.name }))
  return { ...mapDay(book, key, (d) => ({ ...d, threads })), threads: [...(book.threads || []), ...added] }
}

/** The seeker's other threads, to add to a day ("Or add: …"). */
export function otherThreads (book, dayThreads) {
  const on = new Set((dayThreads || []).map((t) => t.id))
  return (book.threads || []).filter((t) => !on.has(t.id))
}
